import { useMemo, useEffect, useCallback } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm, Controller } from "react-hook-form";
import { format } from "date-fns";

import KaboodleModal from "components/Modals/KaboodleModal";
import Button from "components/Button/Button";

import type { OptionType } from "components/inputs";
import { TextInput, DatePicker } from "components/inputs";

import { useCreateClaimantMutation } from "services/claimant-api.ts";
import { useToken } from "utils/context.ts";
import { useNavigate, useLocation } from "react-router-dom";
import Card from "components/Card/Card.tsx";
import { DATE_FORMAT } from "utils/date-utils";
import type { GenderType, StateType } from "types/DataTypes";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

type FormData = {
  first_name: string;
  last_name: string;
  gender: OptionType;
  ssn: string;
  birth_date: string;
  address: string;
  state: OptionType;
  zip_code: string;
};

const CreateClaimantModal = ({ isOpen, onClose }: ModalProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken } = useToken();

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const [createClaimant, { isSuccess }] = useCreateClaimantMutation();

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [onClose, isSuccess]);

  useEffect(() => {
    reset({
      first_name: "",
      last_name: "",
      birth_date: "",
    });
  }, [isOpen, reset]);

  const onSubmit: SubmitHandler<FormData> = useCallback(
    async (data) => {
      const passedData = {
        first_name: data.first_name,
        last_name: data.last_name,
        ssn: data.ssn,
        birth_date: data.birth_date,
        gender: data.gender?.value as GenderType,
        address: data.address,
        state: data.state?.value as StateType,
        zip_code: data.zip_code,
      };
      try {
        const result = await createClaimant({
          data: passedData,
        }).unwrap();

        if (result.id) {
          reset();
          onClose();
          if (location.pathname.includes("claimant")) {
            navigate(`${location.pathname}/${result.id}/overview`);
          }
        }
      } catch (err) {
        console.error("Failed to create claimant:", err);
      }
    },
    [createClaimant, accessToken, reset, onClose, navigate, location],
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const footerContent = useMemo(() => {
    return <Button onClick={handleSubmit(onSubmit)}>Save</Button>;
  }, [handleSubmit, onSubmit]);

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <Card heading="Create Claimant">
        <Controller
          control={control}
          name="first_name"
          rules={{ required: "First name is required." }}
          render={({ field }) => {
            return (
              <TextInput
                {...field}
                placeholder="First Name"
                label="First Name"
                required={true}
                errors={errors}
              />
            );
          }}
        />
        <Controller
          control={control}
          name="last_name"
          rules={{ required: "Last name is required." }}
          render={({ field }) => {
            return (
              <TextInput
                {...field}
                placeholder="Last Name"
                label="Last Name"
                required={true}
                errors={errors}
              />
            );
          }}
        />
        <Controller
          name="birth_date"
          control={control}
          rules={{ required: "Date of birth is required" }}
          render={({ field: { name, onChange, value } }) => (
            <DatePicker
              name={name}
              label="Date of Birth"
              initialDate={value ? new Date(value) : undefined}
              onDateChange={(date) =>
                onChange(date ? format(date, DATE_FORMAT) : null)
              }
              errors={errors}
            />
          )}
        />
      </Card>
    </KaboodleModal>
  );
};

export default CreateClaimantModal;
